import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const includes = [
  'Fully bespoke training blocks delivered through your coaching app',
  'Weekly check-ins, feedback, and data-led programme adjustments',
  'Exercise video library with technical coaching cues',
  'Tailored macro and nutrition guidance aligned to your goals',
  'Message support for questions, form checks, and accountability',
  'Long-term progression planning for sustainable results',
];

const steps = [
  {
    number: '01',
    title: 'Apply & Assess',
    body: 'We map your goals, training environment, time availability, and previous experience in detail.',
  },
  {
    number: '02',
    title: 'Programme Built For You',
    body: 'Laura creates your bespoke plan and coaching workflow inside your app with clear targets.',
  },
  {
    number: '03',
    title: 'Coach, Check-In, Progress',
    body: 'You train with structure while Laura reviews, adjusts, and guides every stage of your journey.',
  },
];

const forYou = [
  'You want an online personal trainer UK clients trust for real results',
  'You need flexibility around travel, work, or family commitments',
  'You still want close support and high accountability remotely',
  'You value clear structure over random workouts',
  'You want a professional online fitness coach in your corner',
];

const expectations = [
  'A clear weekly training framework and progress milestones',
  'Personal check-ins and direct communication with Laura',
  'Fast feedback on execution and recovery strategy',
  'A programme that evolves as your strength and physique improve',
  'Sustainable habits that fit your daily routine',
];

export default function OnlineTraining() {
  useSEO({
    title: 'Online Personal Trainer UK | Bespoke Coaching',
    description:
      'World-class online personal training from anywhere in the UK. Bespoke programmes, weekly check-ins & 24/7 support from Laura Martin.',
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/online-coaching.jpg"
              alt="Online personal trainer UK coaching client through fitness app"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <div className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <span className="inline-block px-4 py-1.5 bg-[#c9a96e]/20 border border-[#c9a96e]/40 text-[#c9a96e] text-[9px] font-sans font-semibold tracking-[0.3em] uppercase rounded-full mb-5">
                Online Coaching · UK
              </span>
              <h1 className="text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.6rem, 5.5vw, 4.5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                Bespoke Online
                <br />
                <em className="text-[#c9a96e] not-italic">Coaching</em>
              </h1>
              <p className="text-white/70 font-sans text-base max-w-xl">
                High-performance online coaching built around your life, with elite structure and direct support every week.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#faf8f5] py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-start">
            <ScrollReveal>
              <div>
                <h2 className="text-stone-900 text-4xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Elite Coaching, Anywhere
                </h2>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  As an online personal trainer UK clients rely on for structure and accountability, Laura works as your dedicated online fitness coach with a complete system that fits your location and schedule.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  You receive a tailored training plan, ongoing programme refinements, and direct expert support that keeps your progress consistent week after week.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed">
                  If you want an online fitness coach who balances precision, flexibility, and real-world sustainability, this programme is designed for you.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={120}>
              <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
                <h3 className="text-stone-900 text-2xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {includes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm leading-relaxed">
                      <span className="w-5 h-5 rounded-full bg-[#c9a96e]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#c9a96e] text-[10px]" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-12">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                How It Works
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.title} delay={index * 80}>
                  <div className="h-full bg-white rounded-2xl border border-stone-200 p-8">
                    <span className="text-6xl leading-none text-[#c9a96e]/20 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.number}
                    </span>
                    <h3 className="text-stone-900 text-xl mt-4 mb-3 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.title}
                    </h3>
                    <p className="text-stone-600 font-sans text-sm leading-relaxed">{step.body}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-8">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Is This Right For You?
              </h2>
            </ScrollReveal>
            <div className="bg-white border border-stone-200 rounded-2xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  This Is For You If...
                </h3>
                <ul className="space-y-3">
                  {forYou.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What To Expect...
                </h3>
                <ul className="space-y-3">
                  {expectations.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-stone-100 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <ScrollReveal>
              <h2 className="text-stone-900 text-5xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ready To Work With Laura Online?
              </h2>
              <p className="text-stone-500 font-sans text-base max-w-2xl mx-auto mb-10">
                Book a free consultation and get a clear strategy for your training, nutrition, and next transformation phase.
              </p>
              <Link
                to="/contact"
                className="inline-block px-9 py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.28em] uppercase rounded-full hover:bg-[#a8863f] transition-all duration-300"
              >
                Book Free Consultation
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
